import { useEffect, useRef, useState } from 'react'
import { alertError, alertSuccess } from 'api'
import { TrainedQuestion } from '../types'
import { UnknownQuestionCreateAPI } from '../../../../../../api/types'
import { unknownQuestionService } from '../../../../../../api/unknownQuestions'
import { userFeedbackService } from '../../../../api/userFeedback'

interface UseSubQuestionLogicParams {
  subQuestionId: string
  feedbackId: string
  initialTrainedQuestions: TrainedQuestion[]
  createNewQuestionCallback: (question: Partial<UnknownQuestionCreateAPI>) => Promise<any>
  setSolvedCallback: (val: boolean) => void
  modifyQuestions: (questions: TrainedQuestion[], subQuestionId: string) => void
}

export const useSubQuestionLogic = ({
  subQuestionId,
  feedbackId,
  initialTrainedQuestions,
  createNewQuestionCallback,
  setSolvedCallback,
  modifyQuestions,
}: UseSubQuestionLogicParams) => {
  const [expanded, setExpanded] = useState(false)
  const [trainedQuestionExpanded, setTrainedQuestionExpanded] = useState(false)
  const [questionCreated, setQuestionCreated] = useState(false)
  const [trainedQuestions, setTrainedQuestions] = useState<TrainedQuestion[]>(initialTrainedQuestions)

  const prevTrainedQuestionsRef = useRef(JSON.stringify(initialTrainedQuestions))

  useEffect(() => {
    const currentString = JSON.stringify(trainedQuestions)

    if (prevTrainedQuestionsRef.current !== currentString) {
      modifyQuestions(trainedQuestions, subQuestionId)
      prevTrainedQuestionsRef.current = currentString
    }
  }, [trainedQuestions, subQuestionId, modifyQuestions])

  const handleToggleExpand = () => {
    setExpanded(prev => !prev)
  }

  const handleTrainingQuestionToggleExpand = () => {
    setTrainedQuestionExpanded(prev => !prev)
  }

  const deleteTrainedQuestion = async (id: string) => {
    try {
      await unknownQuestionService.deleteQuestionById(id)
      setTrainedQuestions(prev => prev.filter(q => q.trainingQuestionId !== id))
    } catch {
      alertError('Failed to delete question. Please try again.')
    }
  }

  const updateQuestion = async (
    id: string,
    questionBeforeUpdate: string,
    questionForUpdate: string,
    answer: string,
    questionType: string
  ) => {
    try {
      await unknownQuestionService.updateQuestion({ id, questionBeforeUpdate, questionForUpdate, answer, type: questionType }, true)
      await unknownQuestionService.trainQuestion({ id, followUp: false }, false)
      setTrainedQuestions(prev =>
        prev.map(q => (q.trainingQuestionId === id ? { ...q, question: questionForUpdate, answer } : q)),
      )
    } catch {
      alertError('Failed to update question. Please try again.')
    } finally {
      setQuestionCreated(true)
    }
  }

  const handleCreationNewQuestion = async (question: string, answer: string, type: string) => {
    try {
      const res = await createNewQuestionCallback({
        question,
        answer,
        type,
        train: false,
      })

      if (res && res.id) {
        await userFeedbackService.pushQuestionToTheList(feedbackId, {
          subQuestionId,
          idToPush: res.id,
          toSubQuestion: true,
        })
        alertSuccess('Training Question is added')

        setTrainedQuestions(prev => [
          ...prev,
          {
            trainingQuestionId: res.id,
            question,
            answer,
            questionType: type
          },
        ])
      }
    } catch {
      alertError('Something went wrong. Please try again.')
    } finally {
      setSolvedCallback(true)
      setQuestionCreated(true)
    }
  }

  return {
    expanded,
    trainedQuestionExpanded,
    questionCreated,
    trainedQuestions,
    handleToggleExpand,
    handleTrainingQuestionToggleExpand,
    handleCreationNewQuestion,
    deleteTrainedQuestion,
    updateQuestion,
    setQuestionCreated,
  }
}
